/**
 * @file Professional Registration Page — /register/professional
 */
import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink, useSearchParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import OAuthButtons from "../components/OAuthButtons";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input, Checkbox } from "../ui";

/**
 * Parse backend Celebrate/Joi validation errors into a Formik-compatible
 * { fieldName: 'mensaje en español' } map. Returns {} when the response is
 * not a per-field validation error (network errors, 500s, etc.) so the
 * caller can fall back to the generic alert.
 */
function parseFieldErrors(err) {
  const details = err?.response?.data?.validation?.body?.details;
  if (!Array.isArray(details) || details.length === 0) return {};

  const out = {};
  for (const d of details) {
    const field = d?.path?.[0];
    if (!field || field in out) continue;
    const limit = d?.context?.limit;
    switch (d.type) {
      case "any.required":
      case "string.empty":
        out[field] = "Este campo es obligatorio";
        break;
      case "string.email":
        out[field] = "Correo electrónico inválido";
        break;
      case "string.min":
        out[field] = `Mínimo ${limit ?? "?"} caracteres`;
        break;
      case "string.max":
        out[field] = `Máximo ${limit ?? "?"} caracteres`;
        break;
      case "any.only":
        out[field] = field === "acceptTerms"
          ? "Debes aceptar los términos y condiciones"
          : field === "confirmPassword"
          ? "Las contraseñas no coinciden"
          : "Valor inválido";
        break;
      default:
        out[field] = "Valor inválido";
    }
  }
  return out;
}

const schema = Yup.object({
  firstName: Yup.string().required(S.REQUIRED_FIELD),
  lastName: Yup.string().required(S.REQUIRED_FIELD),
  email: Yup.string().email(S.INVALID_EMAIL).required(S.REQUIRED_FIELD),
  password: Yup.string().min(10, S.PASSWORD_MIN).required(S.REQUIRED_FIELD),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], S.PASSWORDS_DONT_MATCH)
    .required(S.REQUIRED_FIELD),
  acceptTerms: Yup.boolean().oneOf([true], S.ACCEPT_TERMS_REQUIRED).required(S.ACCEPT_TERMS_REQUIRED),
});

export default function ProfessionalRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";
  const planIntent = searchParams.get("plan") || "";
  const { handleAuthSuccess } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (planIntent) localStorage.setItem("pendingPlan", planIntent);
  }, [planIntent]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      setError("");
      await authService.professionalRegister({
        ...values,
        referralCode: referralCode || undefined,
      });
      setSuccess(true);
    } catch (err) {
      const fieldErrors = parseFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
      } else {
        setError(err.response?.data?.message || "Error al crear la cuenta");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <AuthShell title={S.VERIFY_EMAIL_TITLE} subtitle={S.VERIFY_EMAIL_SUBTITLE}>
        <div className="auth-status">
          <CheckCircle2 size={42} className="auth-status-ok" />
          <Button asChild size="lg" fullWidth>
            <RouterLink to="/login">{S.PRO_LOGIN}</RouterLink>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={S.PRO_REGISTER_TITLE}
      subtitle={S.PRO_REGISTER_SUBTITLE}
      maxWidth={520}
    >
      <p className="auth-oauth-consent">
        Al continuar con Google o Apple, aceptas nuestros{" "}
        <a href="https://consultto.mx/terminos" target="_blank" rel="noopener noreferrer">Términos</a>{" "}
        y el{" "}
        <a href="https://consultto.mx/aviso-de-privacidad" target="_blank" rel="noopener noreferrer">Aviso de Privacidad</a>.
      </p>
      <OAuthButtons
        role="PROFESIONAL"
        referralCode={referralCode}
        acceptTerms
        onSuccess={(data) => {
          handleAuthSuccess(data);
          navigate("/");
        }}
        onError={(msg) => setError(msg)}
      />
      <div className="auth-divider">o regístrate con correo</div>
      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
          acceptTerms: false,
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
          <Form noValidate className="auth-form">
            <div className="auth-form-row">
              <Input
                name="firstName"
                label={S.PRO_FIRST_NAME}
                autoComplete="given-name"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.firstName && errors.firstName}
              />
              <Input
                name="lastName"
                label={S.PRO_LAST_NAME}
                autoComplete="family-name"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.lastName && errors.lastName}
              />
            </div>
            <Input
              name="email"
              type="email"
              label={S.PRO_EMAIL}
              autoComplete="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
            />
            <Input
              name="password"
              type="password"
              label={S.PRO_PASSWORD}
              autoComplete="new-password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && errors.password}
            />
            <Input
              name="confirmPassword"
              type="password"
              label={S.PRO_CONFIRM_PASSWORD}
              autoComplete="new-password"
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.confirmPassword && errors.confirmPassword}
            />
            <Checkbox
              checked={values.acceptTerms}
              onCheckedChange={(checked) => setFieldValue("acceptTerms", !!checked)}
              label={
                <span>
                  {S.ACCEPT_TERMS}{" "}
                  <a href="https://consultto.mx/terminos" target="_blank" rel="noreferrer" className="auth-link">
                    {S.TERMS_LINK}
                  </a>{" "}
                  {S.AND}{" "}
                  <a href="https://consultto.mx/aviso-de-privacidad" target="_blank" rel="noreferrer" className="auth-link">
                    {S.PRIVACY_LINK}
                  </a>
                </span>
              }
            />
            {touched.acceptTerms && errors.acceptTerms && (
              <span className="auth-checkbox-error">{errors.acceptTerms}</span>
            )}
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              {S.PRO_SUBMIT}
            </Button>
          </Form>
        )}
      </Formik>

      <div className="auth-links">
        <span className="auth-link-row">
          {S.PRO_HAS_ACCOUNT}{" "}
          <RouterLink to="/login/profesional" className="auth-link">
            {S.PRO_LOGIN}
          </RouterLink>
        </span>
      </div>
    </AuthShell>
  );
}
